/**
 * Category / section colors for vehicle build surfaces (web).
 * Keep in sync with packages/design-tokens/src/sectionColors.ts (mobile uses the package).
 * CRA cannot transpile workspace TS packages, so web keeps a local copy.
 */

export const CATEGORY_COLOR_MAP = {
  Engine: '#F97316',
  Turbo: '#EA580C',
  'Forced Induction': '#EA580C',
  Drivetrain: '#FB923C',
  'Fuel System': '#D97706',
  Fuel: '#D97706',
  Cooling: '#78716C',
  Suspension: '#A8A29E',
  Brakes: '#E11D48',
  Wheels: '#9A3412',
  'Wheels and Tires': '#9A3412',
  Exhaust: '#C2410C',
  Interior: '#57534E',
  Exterior: '#B45309',
  Electronics: '#F59E0B',
  Safety: '#F43F5E',
  Chassis: '#A16207',
  Performance: '#F59E0B',
  Media: '#A8A29E',
  Events: '#FB923C',
  Passport: '#D97706',
  Recognition: '#F97316',
  Contributors: '#A8A29E',
  Shops: '#B45309',
  General: '#6B7280',
  Miscellaneous: '#6B7280',
} as const;

export type CategoryColorName = keyof typeof CATEGORY_COLOR_MAP;

export const SECTION_FALLBACK_COLOR = '#6B7280';

export function normalizeSectionKey(name: string): string {
  return name.trim().toLowerCase().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ');
}

const LOOKUP: Record<string, string> = Object.fromEntries(
  Object.entries(CATEGORY_COLOR_MAP).map(([k, v]) => [normalizeSectionKey(k), v])
);

export function resolveSectionColor(name: string | null | undefined): string {
  if (!name) return SECTION_FALLBACK_COLOR;
  return LOOKUP[normalizeSectionKey(name)] || SECTION_FALLBACK_COLOR;
}

function parseHex(hex: string): { r: number; g: number; b: number } | null {
  const raw = hex.replace('#', '').trim();
  if (raw.length === 3) {
    const r = parseInt(raw[0] + raw[0], 16);
    const g = parseInt(raw[1] + raw[1], 16);
    const b = parseInt(raw[2] + raw[2], 16);
    return Number.isFinite(r) ? { r, g, b } : null;
  }
  if (raw.length === 6) {
    const r = parseInt(raw.slice(0, 2), 16);
    const g = parseInt(raw.slice(2, 4), 16);
    const b = parseInt(raw.slice(4, 6), 16);
    return Number.isFinite(r) ? { r, g, b } : null;
  }
  return null;
}

export function withAlpha(hex: string, alpha: number): string {
  const rgb = parseHex(hex);
  if (!rgb) return hex;
  const a = Math.max(0, Math.min(1, alpha));
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${a})`;
}

export interface SectionSurfaceTokens {
  color: string;
  border: string;
  borderHover: string;
  borderActive: string;
  tint: string;
  tintHover: string;
  text: string;
  focusRing: string;
}

export function sectionSurfaceTokens(hex: string): SectionSurfaceTokens {
  const color = hex || SECTION_FALLBACK_COLOR;
  return {
    color,
    border: withAlpha(color, 0.32),
    borderHover: withAlpha(color, 0.55),
    borderActive: withAlpha(color, 0.72),
    tint: withAlpha(color, 0.07),
    tintHover: withAlpha(color, 0.12),
    text: color,
    focusRing: withAlpha(color, 0.45),
  };
}

export function sectionSurfaceCssVars(hex: string): Record<string, string> {
  const t = sectionSurfaceTokens(hex);
  return {
    '--section-color': t.color,
    '--section-border': t.border,
    '--section-border-hover': t.borderHover,
    '--section-border-active': t.borderActive,
    '--section-tint': t.tint,
    '--section-tint-hover': t.tintHover,
    '--section-text': t.text,
    '--section-focus': t.focusRing,
  };
}
